import { httpClient } from "./httpClient";

export class FetchEventSource extends EventTarget {
  url: string;

  constructor(url: string) {
    super();
    this.url = url;
    this.start();
  }

  start() {
    httpClient(this.url)
      .then((response) => {
        if (!response.body) {
          this.dispatchEvent(
            new CustomEvent("error", {
              detail: { message: "No response body" },
            }),
          );
          return;
        }
        const reader = response.body.getReader();
        return this.processStream(reader);
      })
      .catch((error) => {
        this.dispatchEvent(
          new CustomEvent("error", { detail: { message: error.message } }),
        );
      });
  }
  /**
   * This function follows the following spec: https://html.spec.whatwg.org/multipage/server-sent-events.html
   * @param reader
   */
  processStream(reader: ReadableStreamDefaultReader<Uint8Array>) {
    const decoder = new TextDecoder();
    let buffer = "";
    let eventName = "message"; // Default event name is "message"
    let closed = false;

    const push = () => {
      reader
        .read()
        .then(({ done, value }) => {
          if (done && !closed) {
            this.dispatchEvent(new Event("end"));
          }

          if (done) {
            return;
          }

          buffer += decoder.decode(value, { stream: true });

          const lines = buffer.split("\n");
          buffer = lines.pop() || ""; // Save the last partial line for the next read
          let dataBuffer = ""; // Accumulate multi-line data
          lines.forEach((line) => {
            line = line.trim();
            if (line.startsWith("data:")) {
              // Append the data (and possibly newlines if multi-line)
              dataBuffer += line.slice(5) + "\n";
            } else if (line.startsWith("event:")) {
              // If event type is specified, change event name
              eventName = line.slice(6).trim();
              if (eventName === "end") {
                // If the the event name is "end", set a closed flag to true, so when the next read is done, it will not dispatch another "end" event
                closed = true;
              }
            } else if (line === "") {
              // An empty line signifies the end of an event block
              if (dataBuffer.trim()) {
                // Dispatch the event with the accumulated data
                const event =
                  eventName === "message"
                    ? new MessageEvent(eventName, {
                        data: dataBuffer.trim(),
                      })
                    : new CustomEvent(eventName, {
                        detail: dataBuffer.trim(),
                      });
                this.dispatchEvent(event);
                // Reset for the next event
                eventName = "message"; // Default back to "message"
                dataBuffer = "";
              }
            }
          });

          push();
        })
        .catch((error) => {
          this.dispatchEvent(
            new ErrorEvent("error", { message: error.message }),
          );
        });
    };

    push();
  }

  close() {
    this.dispatchEvent(new Event("end"));
  }

  onerror!: EventSource["onerror"];
  onmessage!: EventSource["onmessage"];
  onopen!: EventSource["onopen"];
  readyState!: EventSource["readyState"];
  withCredentials!: EventSource["withCredentials"];
  CONNECTING!: EventSource["CONNECTING"];
  OPEN!: EventSource["OPEN"];
  CLOSED!: EventSource["CLOSED"];
}
